import React, { useState } from 'react'
import axios from 'axios'
import { t } from '../../../i18n'
import extractServerErrors from '../extractServerErrors'
import { toSelectOption } from '../selectOptions'
import CommentForm from './CommentForm'
import StarRating from './StarRating'
import {
  Comment,
  CommentAbilities,
  CommentFormValues,
  CommentTagging
} from './types'

interface CommentsPanelProps {
  commentsUrl: string
  tagOptionsUrl: string
  initialComments: Comment[]
  abilities: CommentAbilities
  tagging: CommentTagging | null
  placeholder: string
}

const emptyFormValues: CommentFormValues = {
  rating: 0,
  body: '',
  tag_ids: []
}

const CommentsPanel: React.FC<CommentsPanelProps> = ({
  commentsUrl,
  tagOptionsUrl,
  initialComments,
  abilities,
  tagging,
  placeholder
}) => {
  const [comments, setComments] = useState<Comment[]>(initialComments)
  const [showComposer, setShowComposer] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const extractErrors = (error: unknown): string[] =>
    extractServerErrors(error, t('comments.save_failed'))

  const commentPayload = (values: CommentFormValues) => ({
    comment: {
      rating: values.rating,
      body: values.body,
      tag_ids: values.tag_ids
    }
  })

  const createComment = (
    values: CommentFormValues,
    onError: (messages: string[]) => void
  ) => {
    axios
      .post<Comment>(commentsUrl, commentPayload(values))
      .then((resp) => {
        setComments((currentComments) => [resp.data, ...currentComments])
        setShowComposer(false)
      })
      .catch((error) => onError(extractErrors(error)))
  }

  const updateComment = (
    id: number,
    values: CommentFormValues,
    onError: (messages: string[]) => void
  ) => {
    axios
      .put<Comment>(`${commentsUrl}/${id}`, commentPayload(values))
      .then((resp) => {
        setComments((currentComments) =>
          currentComments.map((comment) =>
            comment.id === id ? resp.data : comment
          )
        )
        setEditingId(null)
      })
      .catch((error) => onError(extractErrors(error)))
  }

  const deleteComment = (id: number) => {
    if (!window.confirm(t('comments.delete_confirm'))) return

    axios
      .delete(`${commentsUrl}/${id}`)
      .then(() =>
        setComments((currentComments) =>
          currentComments.filter((comment) => comment.id !== id)
        )
      )
      .catch(() => window.alert(t('comments.delete_failed')))
  }

  const averageRating =
    comments.length > 0
      ? comments.reduce((sum, comment) => sum + comment.rating, 0) /
        comments.length
      : 0

  const tagLabel = (tag: Comment['tags'][number]) =>
    tag.organization_name ? `${tag.name} (${tag.organization_name})` : tag.name

  const renderComment = (comment: Comment) => {
    if (editingId === comment.id) {
      return (
        <CommentForm
          key={comment.id}
          initialValues={{
            rating: comment.rating,
            body: comment.body,
            tag_ids: comment.tags.map((tag) => tag.id)
          }}
          initialTags={comment.tags.map(toSelectOption)}
          initialOrganizationId={comment.tags[0]?.organization_id ?? null}
          tagging={tagging}
          tagOptionsUrl={tagOptionsUrl}
          placeholder={placeholder}
          submitLabel={t('comments.submit')}
          onSubmit={(values, onError) =>
            updateComment(comment.id, values, onError)
          }
          onCancel={() => setEditingId(null)}
        />
      )
    }

    return (
      <div key={comment.id} className="comment-card">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <strong>{comment.author_name}</strong>
            <div className="comment-card__meta">
              {comment.created_at}
              {comment.edited && (
                <span className="ms-1">{t('comments.edited')}</span>
              )}
            </div>
          </div>
          <StarRating value={comment.rating} size={14} />
        </div>
        <div className="comment-card__body mt-2">{comment.body}</div>
        {tagging && comment.tags.length > 0 && (
          <div className="mt-2">
            <span className="badge bg-light text-dark comment-card__tags">
              {tagging.label}: {comment.tags.map(tagLabel).join(', ')}
            </span>
          </div>
        )}
        {(abilities.canUpdate || abilities.canDelete) && (
          <div className="d-flex justify-content-end mt-1">
            {abilities.canUpdate && (
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={() => setEditingId(comment.id)}
              >
                {t('comments.edit')}
              </button>
            )}
            {abilities.canDelete && (
              <button
                type="button"
                className="btn btn-link btn-sm text-danger"
                onClick={() => deleteComment(comment.id)}
              >
                {t('comments.delete')}
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          {t('comments.title')}
          {comments.length > 0 && (
            <span className="comments-panel__summary ms-2">
              <StarRating value={averageRating} size={14} />{' '}
              {averageRating.toFixed(1)} ({comments.length})
            </span>
          )}
        </h5>
        {abilities.canCreate && !showComposer && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowComposer(true)}
          >
            + {t('comments.add')}
          </button>
        )}
      </div>
      {showComposer && (
        <CommentForm
          initialValues={emptyFormValues}
          initialTags={[]}
          initialOrganizationId={null}
          tagging={tagging}
          tagOptionsUrl={tagOptionsUrl}
          placeholder={placeholder}
          submitLabel={t('comments.submit')}
          onSubmit={createComment}
          onCancel={() => setShowComposer(false)}
        />
      )}
      <div className="comments-panel__list">
        {comments.length === 0 && !showComposer && (
          <div className="comments-panel__empty">{t('comments.empty')}</div>
        )}
        {comments.map(renderComment)}
      </div>
    </div>
  )
}

export default CommentsPanel
